import { useState } from 'react';
import { useApi } from '../lib/api';
import { CONSOLE, type Method, type Preset } from '../lib/consoleConfig';
import { Banner, ErrorBox } from '../ui';

export default function Console({ source }: { source: 'kromeda' | 'tecdoc' }) {
  const cfg = CONSOLE[source];
  const { base, request } = useApi();
  const first = cfg.presets[0];
  const [method, setMethod] = useState<Method>(first.method);
  const [path, setPath] = useState(first.path);
  const [body, setBody] = useState(first.body ?? '');
  const [session, setSession] = useState('');
  const [resp, setResp] = useState<{ status: number; ms: number; data: unknown } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const resolvedPath = session ? path.replaceAll('{s}', session) : path;
  const fullPath = `/${source}${resolvedPath}`;

  function apply(p: Preset) {
    setMethod(p.method);
    setPath(p.path);
    setBody(p.body ?? '');
    setResp(null);
    setErr('');
  }

  async function send() {
    setErr('');
    let payload: unknown;
    if (method === 'POST' && body.trim()) {
      try {
        payload = JSON.parse(body);
      } catch (e) {
        setErr(`Request body is not valid JSON: ${(e as Error).message}`);
        return;
      }
    }
    setBusy(true);
    const t0 = performance.now();
    const r = await request(method, fullPath, payload);
    const ms = Math.round(performance.now() - t0);
    setBusy(false);
    setResp({ status: r.status, ms, data: r.data });
  }

  return (
    <>
      <Banner icon="⌘">
        <b>Raw operation console</b> — direct passthrough to the {source === 'kromeda' ? 'Kromeda' : 'TecDoc'} upstream.{' '}
        {cfg.note}
      </Banner>

      <div className="blk">
        <div className="blk-body space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {cfg.presets.map((p) => (
              <button key={p.label} className="btn btn-sm" onClick={() => apply(p)}>
                {p.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <select className="input" style={{ width: 'auto' }} value={method} onChange={(e) => setMethod(e.target.value as Method)}>
              <option value="GET">GET</option>
              <option value="POST">POST</option>
            </select>
            {cfg.needsSession && (
              <input
                className="input mono"
                style={{ width: 240 }}
                placeholder="session key → {s}"
                value={session}
                onChange={(e) => setSession(e.target.value)}
              />
            )}
            <input className="input mono" style={{ flex: 1, minWidth: 200 }} value={path} onChange={(e) => setPath(e.target.value)} />
            <button className="btn btn-primary" onClick={send} disabled={busy}>
              {busy ? 'Sending…' : 'Send'}
            </button>
          </div>

          {method === 'POST' && (
            <textarea className="input mono" rows={7} value={body} onChange={(e) => setBody(e.target.value)} placeholder="JSON request body" />
          )}

          <div>
            <div className="text-[11px] uppercase tracking-wider text-faint">Request preview</div>
            <pre className="mono mt-1 overflow-x-auto rounded border border-line bg-panel2 p-2.5 text-[12px]">
              {method} {base || ''}
              {fullPath}
            </pre>
          </div>
          {err && <ErrorBox>{err}</ErrorBox>}
        </div>
      </div>

      {resp && (
        <div className="blk">
          <div className="blk-head">
            <h3 className="text-[13px] font-semibold">Response</h3>
            <span className="text-[11.5px] text-faint">
              <span className={resp.status >= 400 ? 'text-bad' : 'text-ok'}>{resp.status}</span> · {resp.ms} ms
            </span>
          </div>
          <pre className="raw">{JSON.stringify(resp.data, null, 2)}</pre>
        </div>
      )}
    </>
  );
}
